import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import { ArrowDownTrayIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { downloadCombinedStockReport } from "../../store/slices/adminDashboardSlice";
import { RootDispatch, RootState } from "../../store";

interface Props {
  globalDate: string;
}

const CombinedStockDownloadButton: React.FC<Props> = ({ globalDate }) => {
  const dispatch: RootDispatch = useDispatch();

  const { isDownloading } = useSelector(
    (state: RootState) => state.adminDashboard,
  );

  const handleDownload = (format: "csv" | "xlsx") => {
    if (!isDownloading) {
      dispatch(downloadCombinedStockReport({ format, date: globalDate }));
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton
          className={`inline-flex justify-center items-center rounded-lg border shadow-sm px-4 py-2 text-sm font-medium transition-all ${
            isDownloading
              ? "bg-gray-200 text-gray-500 cursor-wait border-gray-300"
              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          }`}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
              Exporting...
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" aria-hidden="true" />
              Download Combined Report
              <ChevronDownIcon
                className="w-5 h-5 ml-2 -mr-1"
                aria-hidden="true"
              />
            </>
          )}
        </MenuButton>
      </div>

      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-30 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => handleDownload("xlsx")}
                  disabled={isDownloading}
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm ${
                    isDownloading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Download XLSX
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => handleDownload("csv")}
                  disabled={isDownloading}
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm ${
                    isDownloading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Download CSV
                </button>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default CombinedStockDownloadButton;
